import { Dialog, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { deleteApiKey } from 'entities/ApiKeys/api';
import { ApiKey } from 'entities/ApiKeys/types';
import { FunctionComponent } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ColorPalette } from 'shared/consts/colorPalette';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Paper } from 'shared/ui/Paper';

interface Props {
	apiKey: ApiKey | null;
	onClose: () => void;
	onSuccess: () => void;
}

const DeleteApiKeyModal: FunctionComponent<Props> = ({ apiKey, onClose, onSuccess }) => {
	const { t } = useTranslation();

	const { mutate, isLoading } = useMutation({
		mutationFn: deleteApiKey,
		onSuccess: () => {
			onSuccess();
			onClose();
		},
		onError: (err: AxiosError) => {
			showErrorNotification(err);
		},
	});

	const handleDelete = () => {
		if (!apiKey) return;
		mutate(apiKey._id);
	};

	return (
		<Dialog
			open={!!apiKey}
			onClose={onClose}
			PaperComponent={Paper}
			PaperProps={{ title: t('public_api_keys.delete_modal_title') as string }}
		>
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '16px' }}>
				{t('public_api_keys.delete_modal_description')}
			</Typography>
			{apiKey && (
				<>
					<Typography variant={'body2'} color={ColorPalette.astronaut} sx={{ marginBottom: '4px' }}>
						<b>{t('public_api_keys.table_name')}:</b> {apiKey.name}
					</Typography>
					<Typography
						variant={'body2'}
						color={ColorPalette.astronaut}
						sx={{ marginBottom: '16px' }}
					>
						<b>{t('public_api_keys.table_key')}:</b> {apiKey.keyPrefix}****
					</Typography>
				</>
			)}
			<Typography variant={'body2'} color="error" sx={{ marginBottom: '24px' }}>
				{t('public_api_keys.delete_modal_warning')}
			</Typography>
			<ActionsForDialog
				cancelLabel={t('shared_texts.cancel')}
				cancelAction={onClose}
				applyLabel={t('public_api_keys.revoke_key_btn')}
				applyActionDisabled={isLoading}
				applyAction={handleDelete}
			/>
		</Dialog>
	);
};

export default DeleteApiKeyModal;
